/**
 * Internationalization utilities for frontend
 * Similar to GitLab's locale module
 */

// Default locale
const DEFAULT_LOCALE = 'en'

// Import all locale files statically so Vite can bundle them
import ruTranslations from './ru.json'

// Map of available translations
const availableTranslations = {
  ru: ruTranslations,
}

// Store translations
let translations = {}
let currentLocale = DEFAULT_LOCALE

/**
 * Load translations for a specific locale
 * @param {string} locale - The locale code (e.g., 'en', 'ru')
 */
export function loadTranslations(locale = DEFAULT_LOCALE) {
  if (locale === DEFAULT_LOCALE) {
    // English is the source language, no translations needed
    translations = {}
    currentLocale = DEFAULT_LOCALE
    return
  }

  // Load from pre-imported translations
  if (availableTranslations[locale]) {
    translations = availableTranslations[locale]
    currentLocale = locale
  } else {
    console.warn(`No translations available for locale: ${locale}`)
    translations = {}
    currentLocale = DEFAULT_LOCALE
  }
}

/**
 * Get the current locale
 */
export function getLocale() {
  return currentLocale
}

/**
 * Basic translation function
 * @param {string} msgid - Message ID (English string)
 * @returns {string} Translated string
 */
export function __(msgid) {
  return translations[msgid] || msgid
}

/**
 * Namespaced translation function
 * @param {string} msg - Namespaced message in format "Namespace|Message" or with two arguments
 * @param {string} [message] - Optional separate message parameter
 * @returns {string} Translated string
 */
export function s__(msg, message) {
  let fullMsg

  if (message) {
    // Called as s__('Namespace', 'Message')
    fullMsg = `${msg}|${message}`
  } else {
    // Called as s__('Namespace|Message')
    fullMsg = msg
  }

  // Try to find translation with namespace
  if (translations[fullMsg]) {
    return translations[fullMsg]
  }

  // Fall back to message without namespace
  const parts = fullMsg.split('|')
  const msgWithoutNamespace = parts[parts.length - 1]

  return translations[msgWithoutNamespace] || msgWithoutNamespace
}

/**
 * Get plural form index for Russian
 * @param {number} n - The count
 * @returns {number} Plural form index (0, 1, or 2)
 */
function getRussianPluralForm(n) {
  if (n % 10 === 1 && n % 100 !== 11) {
    return 0 // 1, 21, 31, etc. - день
  } else if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) {
    return 1 // 2-4, 22-24, etc. - дня
  } else {
    return 2 // 0, 5-20, 25-30, etc. - дней
  }
}

/**
 * Pluralization function
 * @param {string} msgid - Singular form
 * @param {string} msgid_plural - Plural form
 * @param {number} count - Count to determine singular/plural
 * @param {Object} [variables] - Optional object with variables for substitution (e.g., { days: 2 })
 * @returns {string} Translated string with count and variables substituted
 */
export function n__(msgid, msgid_plural, count, variables = {}) {
  if (currentLocale === 'ru') {
    // Russian has 3 plural forms
    const formIndex = getRussianPluralForm(count)
    const key = `${msgid}[${formIndex}]`
    const translated = translations[key] || (formIndex === 0 ? msgid : msgid_plural)
    // Use sprintf to substitute variables, then replace %d or remaining %{...} with count
    let result = sprintf(translated, { ...variables, count })
    result = result.replace(/%d/g, String(count))
    // Replace any remaining %{...} with count as fallback
    result = result.replace(/%\{[^}]+\}/g, String(count))
    return result
  } else {
    // English pluralization (simple: singular vs plural)
    const key = count === 1 ? msgid : msgid_plural
    const translated = translations[key] || key
    // Use sprintf to substitute variables, then replace %d or remaining %{...} with count
    let result = sprintf(translated, { ...variables, count })
    result = result.replace(/%d/g, String(count))
    // Replace any remaining %{...} with count as fallback
    result = result.replace(/%\{[^}]+\}/g, String(count))
    return result
  }
}

/**
 * sprintf-like function for variable substitution
 * @param {string} template - String template with %{variable} placeholders
 * @param {Object} variables - Object with variable values
 * @returns {string} String with variables substituted
 */
export function sprintf(template, variables = {}) {
  return template.replace(/%\{(\w+)\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match
  })
}

/**
 * Translate priority value (handles both uppercase and lowercase)
 * @param {string} priority - Priority value (e.g., "LOWEST", "lowest", "LOW", "low", etc.)
 * @returns {string} Translated priority label
 */
export function translatePriority(priority) {
  if (!priority) return ''
  const upperPriority = priority.toUpperCase()
  const translationKey = `Priority|${upperPriority}`
  return translations[translationKey] || upperPriority
}

/**
 * Escape HTML in strings
 * @param {string} str - String to escape
 * @returns {string} HTML-escaped string
 */
export function escape(str) {
  const div = document.createElement('div')
  div.textContent = str
  return div.innerHTML
}

// Detect user's preferred language from HTML lang attribute or default to English
export function detectUserLocale() {
  // Check HTML lang attribute (can be set by backend based on user preference)
  const htmlLang = document.documentElement.lang
  if (htmlLang && htmlLang !== DEFAULT_LOCALE) {
    return htmlLang
  }
  return DEFAULT_LOCALE
}

// Initialize with detected locale and load translations
const detectedLocale = detectUserLocale()
loadTranslations(detectedLocale)
